/**
 * @fileoverview Shared data-access factories and hook factories for fire-hazard
 * map overlays used by every frontend host.
 * @module fire-hazard-map-layers/fireHazardDataAccess
 *
 * Responsibilities:
 * - Define the fire-hazard and active-fire GeoJSON response contracts.
 * - Clamp viewport bounds and construct overlay query parameters.
 * - Apply consistent success, truncation, failure, and warning telemetry behavior.
 * - Create dependency-injected service and hook boundaries without host globals.
 * Dependencies:
 * - @firesquirrel/core-domain for canonical bounds clamping and its server cap.
 * Architecture:
 * - Package-first factory design; hosts inject endpoint-specific HTTP and telemetry adapters.
 * - Read-only remote reference data; callers retain the last successful map source on failure.
 * Notes:
 * - Endpoint paths stay host-owned because each API mounts these routes differently.
 */

import {
  clampBoundsToServerLimit as clampBounds,
  FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES,
  type FireHazardOverlayBounds,
} from '@firesquirrel/core-domain';

export type { FireHazardOverlayBounds };

/**
 * @contract FireHazardOverlayFeatureCollection
 * Purpose: GeoJSON hazard-zone response rendered by the shared map layers.
 * Fields: type, truncated, and polygon features with hazard metadata.
 * Rules: A truncated response remains usable but must emit visibility telemetry.
 * Sync: Read-only remote reference data; never persisted by this package.
 */
export interface FireHazardOverlayFeatureCollection {
  type: 'FeatureCollection';
  truncated: boolean;
  features: Array<{
    type: 'Feature';
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    properties: {
      source: string;
      hazardLevel: string;
      zoneName: string | null;
      zoneCode: string | null;
      inWUI: boolean;
    };
  }>;
}

/**
 * @contract ActiveFirePerimeterFeatureCollection
 * Purpose: GeoJSON response containing current nationwide active-fire perimeters.
 * Fields: type and polygon features identified by externalId and incidentName.
 * Rules: Features are returned unchanged to preserve API response behavior.
 * Sync: Read-only remote reference data; never persisted by this package.
 */
export interface ActiveFirePerimeterFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    properties: {
      externalId: string;
      incidentName: string | null;
    };
  }>;
}

/**
 * @contract FireHazardOverlayQuery
 * Purpose: Canonical query values sent to a host fire-hazard overlay endpoint.
 * Fields: west, south, east, and north WGS84 bounds.
 * Rules: Values are clamped under the server's maximum span before construction.
 * Sync: Request-scoped and never persisted.
 */
export interface FireHazardOverlayQuery {
  west: number;
  south: number;
  east: number;
  north: number;
}

/**
 * @contract FireHazardHttpAdapter
 * Purpose: Injects a host HTTP client while keeping endpoint handling in the host.
 * Fields: get(endpoint, optional query) returning the decoded response.
 * Rules: The adapter must preserve its host client's authentication and base-URL behavior.
 * Sync: Performs a remote read and does not persist responses.
 */
export interface FireHazardHttpAdapter {
  get<T>(endpoint: string, query?: FireHazardOverlayQuery): Promise<T>;
}

/**
 * @contract FireHazardTelemetryAdapter
 * Purpose: Provides the package with host telemetry and warning sinks.
 * Fields: trackEvent, trackException, and warn.
 * Rules: Adapters must not include sensitive values; event names are package-owned.
 * Sync: Telemetry is observational and does not participate in data synchronization.
 */
export interface FireHazardTelemetryAdapter {
  trackEvent(name: string, measurements: Record<string, number>): void;
  trackException(error: Error, context: string): void;
  warn(message: string): void;
}

/**
 * @contract WebTelemetryClient
 * Purpose: Structural subset of the shared web telemetry client required by this package.
 * Fields: trackEvent and trackException methods.
 * Rules: Hosts inject their configured instance; the package never initializes an SDK.
 * Sync: Telemetry is observational and does not participate in data synchronization.
 */
export interface WebTelemetryClient {
  trackEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>,
  ): void;
  trackException(error: Error, properties?: Record<string, string>): void;
}

/**
 * @process createUrlQueryHttpAdapter
 * Steps: Capture a host URL request function and serialize numeric query values per request.
 * Inputs: A generic authenticated GET function accepting a relative endpoint URL.
 * Outputs: FireHazardHttpAdapter.
 * Failure Handling: Request rejections propagate unchanged to the shared service.
 * Idempotency: REQUIRED for equivalent request function and arguments.
 */
export function createUrlQueryHttpAdapter(
  getByUrl: <T>(url: string) => Promise<T>,
): FireHazardHttpAdapter {
  return {
    get: <T>(endpoint: string, query?: FireHazardOverlayQuery): Promise<T> => {
      const url = query
        ? `${endpoint}?${new URLSearchParams(
            Object.entries(query).map(([key, value]) => [key, String(value)]),
          ).toString()}`
        : endpoint;
      return getByUrl<T>(url);
    },
  };
}

/**
 * @process createFireHazardTelemetryAdapter
 * Steps: Bridge package telemetry calls to a configured shared web telemetry client and logger.
 * Inputs: Host telemetry client and optional warning sink.
 * Outputs: FireHazardTelemetryAdapter.
 * Failure Handling: Telemetry client behavior is preserved; no telemetry errors are swallowed.
 * Idempotency: REQUIRED for equivalent injected dependencies.
 *
 * @logging
 * Events: Forwards package-selected event names, measurements, and exception contexts.
 * Level: info for events, exception for failures, warning for diagnostic messages.
 * Correlation: Preserved by the injected host telemetry client.
 */
export function createFireHazardTelemetryAdapter(
  client: WebTelemetryClient,
  warn: (message: string) => void = console.warn,
): FireHazardTelemetryAdapter {
  return {
    trackEvent: (name, measurements) => client.trackEvent(name, {}, measurements),
    trackException: (error, context) => client.trackException(error, { context }),
    warn,
  };
}

/**
 * @contract FireHazardServiceOptions
 * Purpose: Dependencies and route configuration for a hazard-overlay service instance.
 * Fields: http, telemetry, endpoint, and failureContext.
 * Rules: endpoint and failureContext are host-specific; service behavior remains shared.
 * Sync: Configures remote read behavior only.
 */
export interface FireHazardServiceOptions {
  http: FireHazardHttpAdapter;
  telemetry: FireHazardTelemetryAdapter;
  endpoint: string;
  failureContext: string;
}

/**
 * @contract ActiveFireServiceOptions
 * Purpose: Dependencies and route configuration for an active-fire service instance.
 * Fields: http, telemetry, endpoint, and failureContext.
 * Rules: endpoint and failureContext are host-specific; service behavior remains shared.
 * Sync: Configures remote read behavior only.
 */
export interface ActiveFireServiceOptions {
  http: FireHazardHttpAdapter;
  telemetry: FireHazardTelemetryAdapter;
  endpoint: string;
  failureContext: string;
}

/**
 * @contract FireHazardOverlayService
 * Purpose: Fetches clamped, viewport-scoped hazard overlay data.
 * Fields: fetchFireHazardOverlay.
 * Rules: Errors are observed then rethrown without response fallback or retry.
 * Sync: Remote read only.
 */
export interface FireHazardOverlayService {
  fetchFireHazardOverlay(bounds: FireHazardOverlayBounds): Promise<FireHazardOverlayFeatureCollection>;
}

/**
 * @contract ActiveFirePerimeterService
 * Purpose: Fetches the nationwide active-fire perimeter overlay.
 * Fields: fetchActiveFirePerimeters.
 * Rules: Errors are observed then rethrown without response fallback or retry.
 * Sync: Remote read only.
 */
export interface ActiveFirePerimeterService {
  fetchActiveFirePerimeters(): Promise<ActiveFirePerimeterFeatureCollection>;
}

/**
 * @process clampFireHazardOverlayBounds
 * Steps: Apply the canonical client cap and safety margin to viewport bounds.
 * Inputs: Raw viewport bounds.
 * Outputs: Bounds accepted by the server-side size validation.
 * Failure Handling: Pure arithmetic; invalid numeric inputs remain caller errors.
 * Idempotency: REQUIRED.
 */
export function clampFireHazardOverlayBounds(
  bounds: FireHazardOverlayBounds,
): FireHazardOverlayBounds {
  return clampBounds(bounds, FIRE_HAZARD_OVERLAY_MAX_BBOX_DEGREES);
}

/**
 * @process buildFireHazardOverlayQuery
 * Steps: Clamp raw bounds, then copy them into the canonical HTTP query shape.
 * Inputs: Raw viewport bounds.
 * Outputs: A bounded west/south/east/north query object.
 * Failure Handling: Pure construction; no network or telemetry side effects.
 * Idempotency: REQUIRED.
 */
export function buildFireHazardOverlayQuery(
  bounds: FireHazardOverlayBounds,
): FireHazardOverlayQuery {
  const clamped = clampFireHazardOverlayBounds(bounds);
  return {
    west: clamped.west,
    south: clamped.south,
    east: clamped.east,
    north: clamped.north,
  };
}

/**
 * @process createFireHazardOverlayService
 * Steps: Capture injected dependencies; clamp/query each request; emit success,
 * truncation, or failure telemetry; return responses or rethrow errors.
 * Inputs: Host HTTP adapter, telemetry adapter, endpoint, and failure context.
 * Outputs: A host-scoped FireHazardOverlayService.
 * Failure Handling: Warns, tracks the normalized Error, and rethrows the original value.
 * Idempotency: NOT REQUIRED; each fetch performs one remote request.
 *
 * @localFirst
 * Source of Truth: Host API reference-data endpoint.
 * Write Flow: None.
 * Sync Strategy: None; viewport reads are requested on demand.
 * Conflict Resolution: Not applicable.
 *
 * @logging
 * Events: fire_hazard_overlay_loaded, fire_hazard_overlay_truncated, and exception context.
 * Level: info for success/truncation telemetry; warning and exception on failure.
 * Correlation: Delegated to the injected host telemetry client request/session context.
 *
 * @errorHandling
 * Strategy: Normalize for telemetry, then rethrow the original rejection.
 * User Impact: Caller keeps existing map data unchanged.
 * Logging: REQUIRED through the injected telemetry adapter.
 */
export function createFireHazardOverlayService(
  options: FireHazardServiceOptions,
): FireHazardOverlayService {
  return {
    async fetchFireHazardOverlay(bounds) {
      const startTime = Date.now();
      try {
        const result = await options.http.get<FireHazardOverlayFeatureCollection>(
          options.endpoint,
          buildFireHazardOverlayQuery(bounds),
        );
        options.telemetry.trackEvent('fire_hazard_overlay_loaded', {
          featureCount: result.features.length,
          durationMs: Date.now() - startTime,
        });
        if (result.truncated) {
          options.telemetry.warn(
            '[fireHazardOverlay] Overlay truncated — viewport has more zones than the server cap; only the highest-severity zones are shown.',
          );
          options.telemetry.trackEvent('fire_hazard_overlay_truncated', {
            featureCount: result.features.length,
          });
        }
        return result;
      } catch (caught) {
        const error = caught instanceof Error ? caught : new Error(String(caught));
        options.telemetry.warn(`[fireHazardOverlay] Failed to fetch overlay: ${error.message}`);
        options.telemetry.trackException(error, options.failureContext);
        throw caught;
      }
    },
  };
}

/**
 * @process createActiveFirePerimeterService
 * Steps: Capture injected dependencies; request current perimeters; emit success
 * or failure telemetry; return responses or rethrow errors.
 * Inputs: Host HTTP adapter, telemetry adapter, endpoint, and failure context.
 * Outputs: A host-scoped ActiveFirePerimeterService.
 * Failure Handling: Warns, tracks the normalized Error, and rethrows the original value.
 * Idempotency: NOT REQUIRED; each fetch performs one remote request.
 *
 * @localFirst
 * Source of Truth: Host API reference-data endpoint.
 * Write Flow: None.
 * Sync Strategy: None; reads are requested on demand.
 * Conflict Resolution: Not applicable.
 *
 * @logging
 * Events: fire_active_fires_loaded and exception context.
 * Level: info on success; warning and exception on failure.
 * Correlation: Delegated to the injected host telemetry client request/session context.
 *
 * @errorHandling
 * Strategy: Normalize for telemetry, then rethrow the original rejection.
 * User Impact: Caller keeps existing map data unchanged.
 * Logging: REQUIRED through the injected telemetry adapter.
 */
export function createActiveFirePerimeterService(
  options: ActiveFireServiceOptions,
): ActiveFirePerimeterService {
  return {
    async fetchActiveFirePerimeters() {
      const startTime = Date.now();
      try {
        const result = await options.http.get<ActiveFirePerimeterFeatureCollection>(options.endpoint);
        options.telemetry.trackEvent('fire_active_fires_loaded', {
          featureCount: result.features.length,
          durationMs: Date.now() - startTime,
        });
        return result;
      } catch (caught) {
        const error = caught instanceof Error ? caught : new Error(String(caught));
        options.telemetry.warn(
          `[activeFirePerimeter] Failed to fetch active fire perimeters: ${error.message}`,
        );
        options.telemetry.trackException(error, options.failureContext);
        throw caught;
      }
    },
  };
}

/**
 * @contract UseFireHazardOverlayResult
 * Purpose: Component-facing hazard-overlay hook result.
 * Fields: fetchOverlay.
 * Rules: fetchOverlay retains the injected service function identity.
 * Sync: Remote read only.
 */
export interface UseFireHazardOverlayResult {
  fetchOverlay(bounds: FireHazardOverlayBounds): Promise<FireHazardOverlayFeatureCollection>;
}

/**
 * @contract UseActiveFirePerimetersResult
 * Purpose: Component-facing active-fire hook result.
 * Fields: fetchActiveFires.
 * Rules: fetchActiveFires retains the injected service function identity.
 * Sync: Remote read only.
 */
export interface UseActiveFirePerimetersResult {
  fetchActiveFires(): Promise<ActiveFirePerimeterFeatureCollection>;
}

/**
 * @hook createUseFireHazardOverlay
 * Purpose: Create a host-scoped hook facade from an injected service instance.
 * Returns: A custom hook exposing fetchOverlay.
 * Side Effects: Hook creation has none; invoking fetchOverlay performs the service read.
 * Offline Behavior: Service failures propagate so map callers can retain existing data.
 *
 * @reusable
 * Usage: Instantiate once in a host hook module with that host's service.
 * Rules: Stateful dependency closure; no cross-host singleton or mutable global state.
 */
export function createUseFireHazardOverlay(
  service: FireHazardOverlayService,
): () => UseFireHazardOverlayResult {
  return function useFireHazardOverlay(): UseFireHazardOverlayResult {
    return { fetchOverlay: service.fetchFireHazardOverlay };
  };
}

/**
 * @hook createUseActiveFirePerimeters
 * Purpose: Create a host-scoped hook facade from an injected service instance.
 * Returns: A custom hook exposing fetchActiveFires.
 * Side Effects: Hook creation has none; invoking fetchActiveFires performs the service read.
 * Offline Behavior: Service failures propagate so map callers can retain existing data.
 *
 * @reusable
 * Usage: Instantiate once in a host hook module with that host's service.
 * Rules: Stateful dependency closure; no cross-host singleton or mutable global state.
 */
export function createUseActiveFirePerimeters(
  service: ActiveFirePerimeterService,
): () => UseActiveFirePerimetersResult {
  return function useActiveFirePerimeters(): UseActiveFirePerimetersResult {
    return { fetchActiveFires: service.fetchActiveFirePerimeters };
  };
}
